import { GridReadyEvent, IServerSideDatasource } from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import { ExcelSheetIcon } from '@assets';
import { Box, Stack } from '@atgfr8/atg-ui-tailwind';
import { Button } from '@components/Button';
import {
  columnsToolPanel,
  filtersToolPanel,
} from '@components/Grid/components/constants';
import { useNavigate } from '@solidjs/router';
import {
  DormantAccountInfo,
  exportDormantAccountsData,
  fetchDormantAccountsData,
} from '@store/customers/dormantAccount';
import { ConfigManager } from '@utils/ConfigManager';
import { atgLogo } from '@views/ltl';
import { DateTime } from 'luxon';
import { createSignal } from 'solid-js';

import { CustomerHeader } from '../CustomerHeader';
import { dormantAccountsGrid as classes } from './classes';
import { dormantColumnDefs } from './constants';

export type DormantAccountProps = {
  CustomerName: string;
  LastOrder: string;
  MonthsSinceLastOrder: number;
  CustomerId: number;
  AccountManager: string;
  Office: string;
  OpenBalance: number;
  CreditLimit: number;
  Seasonality: string;
};

const cellStyle = {
  padding: '0px 10px',
  'word-break': 'break-word',
  'white-space': 'normal',
  'line-height': '1.6 ',
};

const DormantAccounts = () => {
  const [gridRef, setGridRef] = createSignal<AgGridSolidRef | null>(null);
  const [exportLoading, setExportLoading] = createSignal(false);

  const navigate = useNavigate();

  const filterTypeMapping = {
    equals: 'EQUALS',
    notEqual: 'NOT_EQUALS',
    greaterThan: 'GREATER_THAN',
    greaterThanOrEqual: 'GREATER_THAN_OR_EQUALS',
    lessThan: 'LESS_THAN',
    lessThanOrEqual: 'LESS_THAN_OR_EQUALS',
    contains: 'CONTAINS',
    notContains: 'NOT_CONTAINS',
    startsWith: 'STARTS_WITH',
    endsWith: 'ENDS_WITH',
    blank: 'BLANK',
    notBlank: 'NOT_BLANK',
    inRange: 'IN_RANGE',
    set: 'EQUALS',
  };

  const transformFilters = (inputFilters: { [key: string]: unknown }) => {
    const outputFilters = Object.keys(inputFilters).map((key) => {
      const filter = inputFilters[key] as {
        filterType: string;
        type: keyof typeof filterTypeMapping;
        filter: string;
        dateFrom?: string | null;
        dateTo?: string | null;
        values?: string[];
      };
      if ((filter as { filterType: string }).filterType === 'date') {
        const dateFilters = [];

        if (Boolean(filter.dateFrom)) {
          dateFilters.push(
            DateTime.fromFormat(
              filter.dateFrom as string,
              filter.dateFrom?.length === 19
                ? 'yyyy-MM-dd HH:mm:ss'
                : 'yyyyy-MM-dd HH:mm:ss',
            ).toFormat('MM/dd/yyyy'),
          );
        }

        if (Boolean(filter.dateTo)) {
          dateFilters.push(
            DateTime.fromFormat(
              filter.dateTo as string,
              filter.dateFrom?.length === 19
                ? 'yyyy-MM-dd HH:mm:ss'
                : 'yyyyy-MM-dd HH:mm:ss',
            ).toFormat('MM/dd/yyyy'),
          );
        }
        return {
          field: key.toLowerCase(),
          filterType:
            filterTypeMapping[
              (filter as { type: keyof typeof filterTypeMapping }).type
            ],
          values: dateFilters,
        };
      }
      if ((filter as { filterType: string }).filterType === 'set') {
        return (
          (filter.values ?? []).length > 0 && {
            field: key.toLowerCase(),
            filterType: 'IN',
            values: filter.values,
          }
        );
      }
      if (
        (filter as { filterType: string }).filterType === 'text' ||
        (filter as { filterType: string }).filterType === 'number'
      ) {
        return {
          field: key.toLowerCase(),
          filterType:
            filterTypeMapping[
              (filter as { type: keyof typeof filterTypeMapping }).type
            ],
          values: [(filter as { filter: string }).filter],
        };
      }
    });

    return outputFilters.filter(Boolean);
  };

  const onGridReady = (params: GridReadyEvent) => {
    setGridRef(params);
    const dataSource = {
      getRows: async (params: {
        api: {
          paginationGetCurrentPage: () => number;
        };
        request: {
          filterModel: {
            [key: string]: {
              filter?: string;
              dateFrom?: string | null;
              dateTo?: null;
              filterType: 'date' | 'text' | 'set';
              type: string;
              values: string[];
            };
          };
          sortModel: {
            colId: string;
            sort: string;
          }[];
        };
        success: (data: {
          rowData: DormantAccountInfo[];
          rowCount: number;
        }) => void;
        fail: () => void;
      }) => {
        let orderBy = '';
        let sortBy = '';
        if (params.request.sortModel.length > 0) {
          orderBy = params.request.sortModel[0].colId.toLowerCase();
          sortBy = params.request.sortModel[0].sort;
        }
        const filteredObject = {
          filters: [...transformFilters(params.request.filterModel)],
          sort: {
            field: orderBy || 'customerid',
            direction: sortBy || 'asc',
          },
          pageNumber: params.api.paginationGetCurrentPage() + 1,
          pageSize: 500,
        };
        gridRef()?.api.showLoadingOverlay();
        const res = await fetchDormantAccountsData(filteredObject);

        if (res) {
          if (res.isSuccess) {
            const data = res.value.data ?? [];
            const isEmpty = data.length === 0;

            params.success({
              rowData: data,
              rowCount: res.value.totalCount,
            });

            if (isEmpty) {
              gridRef()?.api.showNoRowsOverlay();
            } else {
              gridRef()?.api.hideOverlay();
            }
          }
        } else {
          params.fail();
          gridRef()?.api.hideOverlay();
        }
      },
    };
    params.api.setGridOption(
      'serverSideDatasource',
      dataSource as unknown as IServerSideDatasource,
    );
  };

  const exportExcel = async () => {
    try {
      setExportLoading(true);
      const getCurrentFilters = gridRef()?.api.getFilterModel();
      await exportDormantAccountsData({
        filters: transformFilters(getCurrentFilters || {}),
        sort: {
          field: 'customerid',
          direction: 'desc',
        },
      });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      {ConfigManager.showLayout && (
        <CustomerHeader title={'Dormant Customers'} />
      )}
      <Box
        class={`rounded-md! ${
          ConfigManager.showLayout
            ? 'nonMobile:h-[calc(100%-230px)]  small:h-[calc(100%-99px)] tablet:h-[calc(100%-100px)] small-netbook:h-[calc(100%-100px)] netbook:h-[calc(100%-100px)] wide-netbook:h-[calc(100%-40px)] min-h-[400px]'
            : 'h-full'
        } p-2 bg-[#E1ECF2]`}
      >
        <Stack class={classes.stackStyle}>
          <Box class={classes.exportBtnBox}>
            <Button
              label="Export to Excel"
              variant="outlined"
              disabled={exportLoading()}
              isLoading={exportLoading()}
              startIcon={
                <img
                  src={ExcelSheetIcon}
                  alt="Excel Icon"
                  width="20"
                  height="20"
                />
              }
              size="sm"
              onClick={exportExcel}
              dataTestId="dormant-accounts-export-to-excel-button"
            />
          </Box>
          <Box class={classes.muiGridStyle}>
            <AgGridSolid
              ref={gridRef}
              columnDefs={dormantColumnDefs}
              overlayNoRowsTemplate="<span class='ag-overlay-loading-center'>No Data</span>"
              loadingOverlayComponent={atgLogo}
              getRowStyle={(params: { node: { rowIndex: number | null } }) => {
                if (
                  params.node.rowIndex !== null &&
                  params.node.rowIndex % 2 === 1
                ) {
                  return { background: '#026EA133' };
                }
                return undefined;
              }}
              gridOptions={{
                defaultColDef: {
                  flex: 1,
                  filter: true,
                  cellStyle: cellStyle,
                },
                rowModelType: 'serverSide',
                paginationPageSize: 500,
                cacheBlockSize: 500,
                onGridReady: onGridReady,
                pagination: true,
                suppressPaginationPanel: false,
                paginationAutoPageSize: false,
                paginationPageSizeSelector: false,
                sideBar: {
                  toolPanels: [columnsToolPanel, filtersToolPanel],
                },
              }}
              onRowClicked={(e) => {
                const customerId = (e.data as { customerId: number })
                  .customerId;
                if (ConfigManager.showLayout) {
                  customerId && navigate(`/customer/details/${customerId}`);
                } else {
                  customerId &&
                    window.open(
                      `${ConfigManager.domainUrl}/customer/details/${customerId}`,
                      '_blank',
                    );
                }
              }}
              overlayLoadingTemplate={atgLogo}
            />
          </Box>
        </Stack>
      </Box>
    </>
  );
};

export default DormantAccounts;
